import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, LoadingController, ModalController, ToastController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BaseClass } from 'src/app/shared/classes/base';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { ConfigService } from '../shared/services/multi-clients/config.service';
import { CalibrationUserOverviewModal } from '../calibration/components/user-overview/user-overview.modal';
import { ProcessConfig } from '../shared/interfaces/process-config.interface';
import { ProcessStatusEnum } from '../shared/interfaces/process.interface';
import { AuthService } from '../shared/services/auth.service';
import { FinalEvaluationStatus, ManagedUser } from './_interfaces/managed-user.interface';
import { ManagerEvaluationService } from './manager-evaluation.service';
import { Group } from 'src/app/groups/_interfaces/group';

@Component({
    selector: 'app-manager-evaluation',
    templateUrl: './manager-evaluation.page.html',
    styleUrls: ['./manager-evaluation.page.scss']
})
export class ManagerEvaluationPage extends BaseClass implements OnDestroy {

    public processConfig: ProcessConfig;
    public status: ProcessStatusEnum;
    public users: Array<ManagedUser> = [];
    public displayedUsers: Array<ManagedUser> = [];
    public canReopenOrDeleteEvaluation = false;
    public hasCalibration = false;
    public showCalibrationFullReport: boolean;
    public nineboxEnabled: boolean;

    public ManagedUserStatus = FinalEvaluationStatus;

    private _cycleId: number;
    private _cycleParticipantId: number;
    private _searchSubject: Subject<string> = new Subject();

    constructor(
            protected _toastController: ToastController,
            protected _translationService: TranslationService,
            private _router: Router,
            private _activatedRoute: ActivatedRoute,
            private _modalController: ModalController,
            private _evaluationService: ManagerEvaluationService,
            private _authService: AuthService,
            private _alertController: AlertController,
            private _loadingController: LoadingController,
            private _configService: ConfigService
    ) {
        super(_toastController, _translationService);
        this._cycleId = parseInt(
                this._activatedRoute.snapshot.paramMap.get('cycleId'), 10
        );
        this._setSearchSubscription();
        this.showCalibrationFullReport = this._configService.settings.features.showCalibrationFullReport;
        this.nineboxEnabled = this._configService.settings.features.nineBox.enabled;
    }

    ionViewWillEnter() {
        const userRoles = this._authService.getUserRoles();
        this.canReopenOrDeleteEvaluation = userRoles.includes('Administrator') || userRoles.includes('HR');
        this._evaluationService.getTeamListForEvaluation(
                this._cycleId
        ).subscribe((response) => {
            this.processConfig = response.data.config;
            this.status = response.data.status;
            this.users = response.data.users;
            this.displayedUsers = response.data.users;
            this._cycleParticipantId = response.data.cycleParticipantId;
            this.hasCalibration = response.data.hasCalibration;
        }, (error) => this.notifyError(error));
    }

    public goBack(): void {
        this._router.navigate(['dashboard']);
    }

    public updateSearch(searchTextValue: string): void {
        this._searchSubject.next(searchTextValue);
    }

    public goToEvaluation(user: ManagedUser): void {
        this._router.navigate([
            'manager-evaluation/' + this._cycleId + '/' + user.userId
        ]);
    }

    public getTeamComparisonExcel() {
        var search = "";

        this._evaluationService.getTeamComparisonExcel(
                this._cycleId
        ).subscribe(
                (data) =>
                        this.notify(
                                this._translationService.getTranslation('Report.Success'),
                                'success'
                        ),
                (error) => {
                    console.log('Error downloading the file.');
                    console.log(error);
                    this.notify(
                            this._translationService.getTranslation('Report.Error'),
                            'danger'
                    );
                }
        );
    }

    public async reopenEvaluation(user: ManagedUser) {
        const alert = await this._alertController.create({
            message: this._translationService.getTranslation('managerEvaluation.confirmReopen'),
            buttons: [{
                text: this._translationService.getTranslation('generic.no'),
                role: 'cancel',
                cssClass: 'danger'
            }, {
                text: this._translationService.getTranslation('generic.yes'),
                handler: () => this._handleOpenEvaluation(user)
            }]
        });

        await alert.present();
    }

    private async _handleOpenEvaluation(user: ManagedUser) {
        const loading = await this._loadingController.create();
        await loading.present();
        this._evaluationService.reopenManagerEvaluation(
                this._cycleId,
                user.userId
        ).subscribe(() => {
            this.ionViewWillEnter();
            loading.dismiss();

        }, (error) => {
            loading.dismiss();
            this.notifyError(error);
        });
    }

// Funcionalidade desabilitada

    // public async deleteEvaluation(user: ManagedUser) {
    //   let hasEvals = false;
    //   const alert = await this._alertController.create({
    //     message: this._translationService.getTranslation('managerEvaluation.confirmDelete'),
    //     buttons: [{
    //       text: this._translationService.getTranslation('generic.no'),
    //       role: 'cancel',
    //       cssClass: 'danger'
    //     }, {
    //       text: this._translationService.getTranslation('generic.yes'),
    //       handler: async () => {
    //         if (hasEvals) {
    //           this._handleConfirmDeletion(user);
    //         } else {
    //           this._handleDeletion(user);
    //         }
    //       }
    //     }]
    //   });

    //   this._evaluationService.getManagerEvaluationToDelete(
    //     this._cycleId,
    //     user.userId
    //   ).subscribe( async () => {
    //     await alert.present();
    //   }, async (error) => {
    //     hasEvals = true;
    //     await alert.present();
    //   });
    // }

    // private async _handleConfirmDeletion(user: ManagedUser) {
    //   const alert = await this._alertController.create({
    //     message: this._translationService.getTranslation('managerEvaluation.evalsToDelete'),
    //     buttons: [{
    //       text: this._translationService.getTranslation('generic.no'),
    //       role: 'cancel',
    //       cssClass: 'danger'
    //     }, {
    //       text: this._translationService.getTranslation('generic.yes'),
    //       handler: async () => {
    //         this._handleDeletion(user);
    //       }
    //     }]
    //   });
    //   await alert.present();
    // }

    // private async _handleDeletion (user: ManagedUser) {
    //   const loading = await this._loadingController.create();
    //   await loading.present();
    //   this._evaluationService.deleteManagerEvaluation(
    //     this._cycleId,
    //     user.userId
    //   ).subscribe(() => {
    //     this.ionViewWillEnter();
    //     loading.dismiss();

    //   }, (error) => {
    //     loading.dismiss();
    //     this.notifyError(error);
    //   });
    // }

    public async viewUserCalibration(user: ManagedUser): Promise<void> {
        const modal = await this._modalController.create({
            component: CalibrationUserOverviewModal,
            cssClass: 'calibration',
            componentProps: {
                'managerEvaluationView': true,
                'cycleId': this._cycleId,
                'userId': user ? user.userId:null,
                'cycleParticipantId': user ? user.cycleParticipantId:null
            }
        });

        modal.present();
    }


    public viewFullReport(user: ManagedUser): void {
        localStorage.setItem(
                'FinalReport_ReturnPath',
                'manager-evaluation/' + this._cycleId
        );

        this._router.navigate([
            'final-report/' + this._cycleId + '/' + user.userId
        ]);
    }

    private _setSearchSubscription() {
        this._searchSubject.pipe(
                distinctUntilChanged(),
                debounceTime(1000)
        ).subscribe((searchValue: string) => {
            this.displayedUsers = this.users.filter(goal => {
                return goal.name.toLowerCase().includes(
                        searchValue.toLowerCase().trim()
                );
            });
        });
    }

    ngOnDestroy() {
        this._searchSubject.unsubscribe();
    }

}
